/**
 * Vendors list — S14a. Mirrors the customers list shape exactly; only
 * the per-vertical bits (lead-summary copy, KPI tiles, tab labels, row
 * component, action stubs) differ.
 *
 * V1.0.x trims:
 *   - Lead-summary outstanding/awaiting/spent → placeholders (wires up with bills)
 *   - "With dues / Active 30d / Recurring / New" tabs are UI-only stubs
 *   - Reliability filter, Sort by spend → disabled stubs
 *
 * Export (CSV / Excel / PDF) and Import CSV (bulk-create) are wired:
 * export honours the current search + tab and spans the whole filtered set
 * (CSV/Excel client-side off `vendors.exportRows`; PDF server-side via
 * Gotenberg through `vendors.exportPdf`). Import validates each CSV row
 * against `createInput` and bulk-creates via `vendors.import`.
 *
 * @see prototype/purchases/vendors.html — V1.1 baseline
 * @see features/vendors/export, features/vendors/import
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  HeartPulse,
  Import,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search as SearchIcon,
  SlidersHorizontal,
  Table as TableIcon,
} from "lucide-react";
import type { vendors as vendorsContract } from "@flint/api-contract";
import type { z } from "zod";
import { paiseToInrShort } from "../../../../lib/format.ts";
import {
  Alert,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  TabsRoot,
  TabsList,
  TabsTrigger,
  ToggleGroupRoot,
  ToggleGroupItem,
  toast,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import {
  LeadSummary,
  ListPagination,
  MasterListCard,
} from "../../../../features/master-list/index.ts";
import {
  downloadVendorsCsv,
  downloadVendorsPdf,
  downloadVendorsXlsx,
  EmptyState,
  ImportVendorsDialog,
  KpiStrip,
  VendorCard,
  VendorRow,
} from "../../../../features/vendors/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

type ListRow = z.infer<typeof vendorsContract.listRow>;

interface VendorsSearch {
  q?: string | undefined;
  tab?: "all" | "dues" | "active" | "recurring" | "new" | "archived" | undefined;
  view?: "list" | "grid" | undefined;
  reliability?: "all" | "reliable" | "slow" | "flag" | undefined;
  sort?: "recent" | "name" | "spend" | undefined;
}

const TABS: Array<{ id: NonNullable<VendorsSearch["tab"]>; label: string }> = [
  { id: "all", label: "All" },
  { id: "dues", label: "With dues" },
  { id: "active", label: "Active · 30d" },
  { id: "recurring", label: "Recurring" },
  { id: "new", label: "New" },
  { id: "archived", label: "Archived" },
];

export const Route = createFileRoute("/(workspace)/procurement/vendors/")({
  component: VendorsListPage,
  validateSearch: (s: Record<string, unknown>): VendorsSearch => {
    const out: VendorsSearch = {};
    if (typeof s.q === "string" && s.q) out.q = s.q;
    if (s.tab && TABS.some((t) => t.id === s.tab)) {
      out.tab = s.tab as VendorsSearch["tab"];
    }
    if (s.view === "grid" || s.view === "list") out.view = s.view;
    if (
      s.reliability === "reliable" ||
      s.reliability === "slow" ||
      s.reliability === "flag" ||
      s.reliability === "all"
    ) {
      out.reliability = s.reliability;
    }
    if (s.sort === "name" || s.sort === "spend" || s.sort === "recent") out.sort = s.sort;
    return out;
  },
});

function VendorsListPage() {
  const canManage = usePermission("vendors.manage");
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState(search.q ?? "");

  const tab = search.tab ?? "all";
  const view = search.view ?? "list";
  const reliability = search.reliability ?? "all";
  const sort = search.sort ?? "recent";

  // Wire `tab` to the API: only `all` and `archived` correspond to backend
  // filters today. The other tabs (dues / active / recurring / new) are
  // UI-only until bills + payments land.
  const wireTab: "all" | "archived" = tab === "archived" ? "archived" : "all";

  // ── Cursor-stack pagination ─────────────────────────────────────
  // `cursorStack` is the list of nextCursors we've pushed past as the
  // operator clicked Next. Each entry IS the cursor passed to that
  // page's query. Page 1 = stack empty (no cursor sent); page 2 =
  // stack has the cursor returned with page 1; etc.
  //
  // Server returns `nextCursor` when more rows follow. Absence = last
  // page. The pagination component disables Prev on page 1 and Next
  // when nextCursor is missing.
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  // Reset to page 1 whenever the filters that affect the result set
  // change. Without this, paging deeper, then changing search, would
  // request a cursor that doesn't exist in the new result set.
  useEffect(() => {
    setCursorStack([]);
  }, [wireTab, search.q]);

  const list = trpc.vendors.list.useQuery({
    limit: PAGE_SIZE,
    tab: wireTab,
    ...(search.q ? { q: search.q } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
  });

  const counts = trpc.vendors.counts.useQuery();

  // ── Export / import ─────────────────────────────────────────────
  // Export honours the operator's current view (search + tab) and always
  // spans the whole filtered set (server caps it), not just the visible
  // page. CSV + Excel serialise client-side off `exportRows`; PDF renders
  // server-side through Gotenberg (`exportPdf`).
  const utils = trpc.useUtils();
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState<null | "csv" | "xlsx" | "pdf">(null);
  const exportPdf = trpc.vendors.exportPdf.useMutation();

  const exportFilter = {
    tab: wireTab,
    ...(search.q ? { q: search.q } : {}),
  };

  const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
    if (exporting) return;
    setExporting(format);
    try {
      if (format === "pdf") {
        const res = await exportPdf.mutateAsync(exportFilter);
        downloadVendorsPdf(res.pdfBase64, res.fileName);
      } else {
        const data = await utils.vendors.exportRows.fetch(exportFilter);
        if (data.rows.length === 0) {
          toast.info("No vendors match this view to export.");
          return;
        }
        if (format === "csv") downloadVendorsCsv(data.rows, data.generatedAt);
        else await downloadVendorsXlsx(data.rows, data.generatedAt);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed. Please try again.");
    } finally {
      setExporting(null);
    }
  };

  const updateSearch = (patch: Partial<VendorsSearch>) => {
    const next: VendorsSearch = { ...search, ...patch };
    if (!next.q) delete next.q;
    if (next.tab === "all") delete next.tab;
    if (next.view === "list") delete next.view;
    if (next.reliability === "all") delete next.reliability;
    if (next.sort === "recent") delete next.sort;
    void navigate({ to: "/procurement/vendors", search: next });
  };

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateSearch({ q: draftQuery.trim() || undefined });
  };

  const items = (list.data?.items as ListRow[] | undefined) ?? [];
  // Client-side reliability filter (applies to the visible page, consistent
  // with the other client-side filters here). Only the "open bills" bucket has
  // real data today — `outstandingPaise` sums bills in review / approval_pending
  // / ready_to_pay. The reliable/slow buckets stay disabled until the GRN
  // reliability feed lands.
  const filteredItems = reliability === "flag" ? items.filter((v) => BigInt(v.outstandingPaise) > 0n) : items;
  const sortedItems = sortRows(filteredItems, sort);

  // Pagination derivations — page index = stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = items.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + items.length;
  // Total comes from the separate counts query — vendors.list doesn't
  // return a total to keep the page-1 query cheap (cursor APIs don't
  // know the total without a separate COUNT(*) which RLS would also
  // wrap. counts.useQuery is that COUNT(*) already, cached separately).
  const totalRows = wireTab === "archived" ? counts.data?.archived : counts.data?.total;

  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (!next) return;
    setCursorStack((s) => [...s, next]);
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Procurement", to: "/" }, { label: "Vendors" }]}>
      <div className="space-y-[18px]">
        {/* ── Page head ───────────────────────────────────────────── */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">Vendors</h1>
            <LeadSummary
              loading={!counts.data}
              items={
                counts.data
                  ? (() => {
                      // Real aggregations from vendor list items (each row
                      // carries outstandingPaise / ytdSpentPaise / billCount).
                      const items = list.data?.items ?? [];
                      const outstanding = items.reduce(
                        (s, v) => s + BigInt(v.outstandingPaise),
                        0n,
                      );
                      const ytd = items.reduce((s, v) => s + BigInt(v.ytdSpentPaise), 0n);
                      const awaiting = items.filter((v) => BigInt(v.outstandingPaise) > 0n).length;
                      return [
                        { value: counts.data.total, label: "vendors" },
                        {
                          value: paiseToInrShort(outstanding.toString()),
                          label: "outstanding",
                          tone: outstanding > 0n ? "warn" : "muted",
                        },
                        {
                          value: String(awaiting),
                          label: "awaiting payment",
                          tone: awaiting > 0 ? "warn" : "muted",
                        },
                        {
                          value: paiseToInrShort(ytd.toString()),
                          label: "spent YTD",
                          tone: "muted",
                        },
                      ];
                    })()
                  : []
              }
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Hidden on phone — bulk import isn't a phone-first flow. */}
            {canManage && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                title="Bulk-add vendors from a CSV file"
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-line bg-paper text-xs font-semibold text-ink hover:bg-bg"
              >
                <Import size={13} strokeWidth={2} aria-hidden />
                Import CSV
              </button>
            )}
            {canManage && (
              <Link
                to="/procurement/vendors/new"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
              >
                <Plus size={13} strokeWidth={2.4} aria-hidden />
                Add vendor
              </Link>
            )}
          </div>
        </header>

        {/* ── KPI strip ───────────────────────────────────────────── */}
        <KpiStrip
          total={counts.data?.total ?? 0}
          archived={counts.data?.archived ?? 0}
          newThisMonth={counts.data?.newThisMonth ?? 0}
        />

        {/* ── Tabs ────────────────────────────────────────────────── */}
        <TabsRoot
          value={tab}
          onValueChange={(v) => updateSearch({ tab: v as VendorsSearch["tab"] })}
        >
          <TabsList aria-label="Vendor status">
            {TABS.map((t) => {
              const tabCount =
                t.id === "all"
                  ? counts.data?.total
                  : t.id === "archived"
                    ? counts.data?.archived
                    : t.id === "new"
                      ? counts.data?.newThisMonth
                      : 0;
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  count={tabCount}
                  countTone={t.id === "dues" ? "warn" : "default"}
                >
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </TabsRoot>

        {/* Honest banner for stub tabs. */}
        {tab === "dues" || tab === "active" || tab === "recurring" || tab === "new" ? (
          <Alert
            intent="info"
            title={
              tab === "dues"
                ? "With-dues filter wires up with bills"
                : tab === "active"
                  ? "Active-30d filter wires up with bills"
                  : tab === "recurring"
                    ? "Recurring filter wires up with bills + POs"
                    : "New-vendor filter — currently uses created_at < 30 days"
            }
            description={
              tab === "new"
                ? 'Showing all vendors; the count above is the real "added in last 30 days" tally.'
                : "Showing all vendors for now. The narrowed filter activates when the relevant vertical lands."
            }
          />
        ) : null}

        {/* ── Toolbar ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={submitSearch} className="flex-1 min-w-[260px] max-w-[380px]">
            <label className="flex items-center gap-2 h-9 px-3 rounded-sm border border-line bg-bg focus-within:border-sky-600 focus-within:bg-paper focus-within:ring-2 focus-within:ring-sky-100 transition-colors">
              <SearchIcon size={14} strokeWidth={2} aria-hidden className="text-muted shrink-0" />
              <input
                type="search"
                placeholder="Search by name, GSTIN, contact…"
                value={draftQuery}
                onChange={(e) => setDraftQuery(e.target.value)}
                className="flex-1 min-w-0 bg-transparent border-0 outline-0 text-sm placeholder:text-muted text-ink"
                aria-label="Search vendors"
              />
              {search.q ? (
                <button
                  type="button"
                  onClick={() => {
                    setDraftQuery("");
                    updateSearch({ q: undefined });
                  }}
                  className="text-xs text-muted hover:text-ink"
                  aria-label="Clear search"
                >
                  clear
                </button>
              ) : null}
            </label>
          </form>

          {/* Reliability filter — TODO V1.0.x: wires to bills-derived buckets. */}
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg hover:border-line-2"
              >
                <HeartPulse size={12} strokeWidth={2} aria-hidden className="text-muted" />
                Reliability
                <ChevronDown size={12} strokeWidth={2} aria-hidden className="text-muted" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Filter by reliability</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={reliability}
                onValueChange={(v) =>
                  updateSearch({ reliability: v as VendorsSearch["reliability"] })
                }
              >
                <DropdownMenuRadioItem value="all">All vendors</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="flag">Has open bills</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="reliable" disabled>
                  Reliable · needs GRN feed
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="slow" disabled>
                  Slow · needs GRN feed
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenuRoot>

          {/* Sort */}
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg hover:border-line-2"
              >
                <SlidersHorizontal size={12} strokeWidth={2} aria-hidden className="text-muted" />
                <span className="font-medium text-muted">Sort ·</span>
                <span>{sortLabel(sort)}</span>
                <ChevronDown size={12} strokeWidth={2} aria-hidden className="text-muted" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Sort vendors</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={sort}
                onValueChange={(v) => updateSearch({ sort: v as VendorsSearch["sort"] })}
              >
                <DropdownMenuRadioItem value="recent">Most recently added</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name">Name (A → Z)</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="spend">Biggest spend first</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenuRoot>

          <span aria-hidden className="hidden sm:block flex-1" />

          {/* View toggle — hidden on phone (rows + cards collapse to the
              same single-column shape there). */}
          <ToggleGroupRoot
            type="single"
            value={view}
            onValueChange={(v) => {
              if (v) updateSearch({ view: v as VendorsSearch["view"] });
            }}
            aria-label="View mode"
            className="hidden sm:inline-flex"
          >
            <ToggleGroupItem value="list" aria-label="List view">
              <ListIcon size={14} strokeWidth={2} aria-hidden />
            </ToggleGroupItem>
            <ToggleGroupItem value="grid" aria-label="Grid view">
              <LayoutGrid size={14} strokeWidth={2} aria-hidden />
            </ToggleGroupItem>
          </ToggleGroupRoot>

          {/* Export — CSV / Excel (client-side) + PDF (server Gotenberg).
              Honours the current search + tab. Hidden on phone. */}
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={exporting !== null}
                title="Export the current view to CSV, Excel, or PDF"
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg hover:border-line-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download size={12} strokeWidth={2} aria-hidden />
                {exporting ? "Exporting…" : "Export"}
                <ChevronDown size={12} strokeWidth={2} aria-hidden className="text-muted" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export vendors</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => void handleExport("csv")}>
                <TableIcon size={13} strokeWidth={2} aria-hidden className="text-muted" />
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void handleExport("xlsx")}>
                <FileSpreadsheet size={13} strokeWidth={2} aria-hidden className="text-muted" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void handleExport("pdf")}>
                <FileText size={13} strokeWidth={2} aria-hidden className="text-muted" />
                PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        {list.isLoading ? (
          <p className="text-muted">Loading vendors…</p>
        ) : list.error ? (
          <Alert intent="bad" title="Couldn't load vendors" description={list.error.message} />
        ) : sortedItems.length > 0 ? (
          view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sortedItems.map((v) => (
                <VendorCard key={v.id} vendor={v} />
              ))}
            </div>
          ) : (
            <MasterListCard
              header={
                // Aligns with VendorRow's `lg` grid template + padding.
                <div className="grid items-center gap-4 px-5 py-2.5 grid-cols-[44px_minmax(0,1.8fr)_minmax(0,1.2fr)_110px_110px_auto]">
                  <span aria-hidden />
                  <span>Vendor</span>
                  <span>Activity</span>
                  <span className="text-right">We owe</span>
                  <span className="text-right">YTD spend</span>
                  <span aria-hidden />
                </div>
              }
              footer={[
                { label: "Total vendors", value: counts.data?.total ?? 0 },
                { label: "New 30d", value: counts.data?.newThisMonth ?? 0 },
                // TODO V1.0.x — outstanding bill total (warn when > 0).
                { label: "Outstanding", value: "₹—", accent: "default" },
                // TODO V1.0.x — best vendor + top spend from bills.
                { label: "Best payer", value: "—" },
                { label: "Top spend", value: "—" },
              ]}
              pagination={
                <ListPagination
                  pageNumber={pageNumber}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                  {...(typeof totalRows === "number" ? { total: totalRows } : {})}
                  sortLabel={`sorted by ${sortLabel(sort)}`}
                  hasPrev={cursorStack.length > 0}
                  hasNext={!!list.data?.nextCursor}
                  onPrev={handlePrev}
                  onNext={handleNext}
                />
              }
            >
              {sortedItems.map((v) => (
                <VendorRow key={v.id} vendor={v} />
              ))}
            </MasterListCard>
          )
        ) : search.q ? (
          <EmptyState variant="no-search-match" query={search.q} />
        ) : tab === "archived" ? (
          <EmptyState variant="no-archived" />
        ) : (
          <EmptyState variant="first-run" />
        )}
      </div>

      {canManage ? (
        <ImportVendorsDialog open={importOpen} onOpenChange={setImportOpen} />
      ) : null}
    </WorkspaceShell>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function sortLabel(s: NonNullable<VendorsSearch["sort"]>): string {
  switch (s) {
    case "name":
      return "A → Z";
    case "spend":
      return "biggest spend first";
    default:
      return "most recent";
  }
}

function sortRows(rows: ListRow[], sort: NonNullable<VendorsSearch["sort"]>): ListRow[] {
  if (sort === "name") {
    return [...rows].sort((a, b) => a.name.localeCompare(b.name, "en-IN"));
  }
  if (sort === "spend") {
    // Biggest YTD spend first. Amounts are integer-paise strings — compare as
    // bigint so ₹1Cr+ vendors never lose precision to a float.
    return [...rows].sort((a, b) => {
      const av = BigInt(a.ytdSpentPaise);
      const bv = BigInt(b.ytdSpentPaise);
      return av > bv ? -1 : av < bv ? 1 : 0;
    });
  }
  return rows;
}
